import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './context/AuthContext';
import { isStaging } from './lib/api';
import AppLayout from './components/layout/AppLayout';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import AlertsPage from './pages/AlertsPage';
import SettingsPage from './pages/SettingsPage';
import UsersPage from './pages/UsersPage';
import ShelterCardPage from './pages/ShelterCardPage';
import TenantCardPage from './pages/TenantCardPage';
import ServicePage from './pages/ServicePage';

// Vanhat Huolto-osoitteet (#82): /huolto/asiakas/:id ja huoltopohjan /huolto/asiakas/:id/pohja
// (pohja poistui versiossa 5) → Huolto valittuna asiakkaana, /huolto/:id → suojatilakortti,
// jossa huolto kirjataan.
function LegacyServiceRedirect({ to }) {
  const { id } = useParams();
  const target = to === 'shelter' ? `/suojatila/${encodeURIComponent(id)}` : `/huolto?asiakas=${encodeURIComponent(id)}`;
  return <Navigate to={target} replace />;
}

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const { t } = useTranslation();
  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-400 text-sm">
      {t('common.loading')}
    </div>
  );
  if (!user) {
    // Paluuosoite talteen kirjautumisen jälkeen (LoginPage.takeReturnPath, #32)
    const path = window.location.pathname + window.location.search;
    if (path !== '/' && path !== '/dashboard') {
      try { sessionStorage.setItem('safedigi_return_to', path); } catch { /* ei sessionStoragea */ }
    }
    return <Navigate to="/kirjaudu" replace />;
  }
  try { sessionStorage.removeItem('safedigi_return_to'); } catch { /* ei sessionStoragea */ }
  return children;
}

function RequireRole({ roles, children }) {
  const { profile, loading } = useAuth();
  if (loading) return null;
  if (!roles.includes(profile?.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/kirjaudu" element={<LoginPage />} />
      <Route path="/unohtuiko-salasana" element={<ForgotPasswordPage />} />
      <Route path="/aseta-salasana" element={<ResetPasswordPage />} />
      <Route path="/" element={<RequireAuth><AppLayout /></RequireAuth>}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="alerts" element={<AlertsPage />} />
        <Route path="tili" element={<Navigate to="/users" replace />} />
        <Route
          path="settings"
          element={<RequireRole roles={['admin', 'superadmin']}><SettingsPage /></RequireRole>}
        />
        <Route
          path="settings/:id"
          element={<RequireRole roles={['admin', 'superadmin']}><SettingsPage /></RequireRole>}
        />
        <Route path="users" element={<UsersPage />} />
        <Route
          path="suojatila/:id"
          element={<RequireRole roles={['admin', 'superadmin', 'huolto']}><ShelterCardPage /></RequireRole>}
        />
        <Route
          path="asiakas/:id"
          element={<RequireRole roles={['superadmin']}><TenantCardPage /></RequireRole>}
        />
        {/* Huoltokirjaukset (#82): Superadmin ja Huolto-rooli (#86) */}
        <Route
          path="huolto"
          element={<RequireRole roles={['superadmin', 'huolto']}><ServicePage /></RequireRole>}
        />
        <Route
          path="huolto/asiakas/:id"
          element={<RequireRole roles={['superadmin', 'huolto']}><LegacyServiceRedirect to="tenant" /></RequireRole>}
        />
        <Route
          path="huolto/asiakas/:id/pohja"
          element={<RequireRole roles={['superadmin', 'huolto']}><LegacyServiceRedirect to="tenant" /></RequireRole>}
        />
        <Route
          path="huolto/:id"
          element={<RequireRole roles={['superadmin', 'huolto']}><LegacyServiceRedirect to="shelter" /></RequireRole>}
        />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function StagingBanner() {
  const { t } = useTranslation();
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999, background: '#f59e0b', color: '#000', textAlign: 'center', fontSize: '13px', fontWeight: 700, padding: '5px', letterSpacing: '0.05em' }}>
      ⚠ {t('common.stagingBanner')} ⚠
    </div>
  );
}

export default function App() {
  return (
    <>
      {isStaging && <StagingBanner />}
      <div style={isStaging ? { paddingTop: '29px' } : undefined}>
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </BrowserRouter>
      </div>
    </>
  );
}
